import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Building2, UserPlus } from "lucide-react";
import { type ReactElement, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { CompanyDialog } from "@/components/crm/company-dialog";
import { ContactFormDialog } from "@/components/crm/contact-form";
import { CrmLoadError, CrmPageHeader } from "@/components/crm/crm-page";
import { LoadMore, nextOffset } from "@/components/crm/load-more";
import {
	OwnerFilter,
	type OwnerFilterValue,
	resolveOwnerFilter,
} from "@/components/crm/owner-select";
import { SortableHead, useListSort } from "@/components/crm/sortable-head";
import { OrgTypeBadge } from "@/components/crm/status-badges";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { useSession } from "@/hooks/auth-hooks";
import { useCrmPermissions } from "@/hooks/use-crm-access";
import { CRM_ORGANIZATION_TYPES } from "@/lib/crm/constants";
import { CRM_STALE_TIME } from "@/lib/crm/queries";
import type { CrmOrganizationType } from "@/lib/crm/types";
import { orpc } from "@/utils/orpc";

export const Route = createFileRoute("/crm/organizations/")({
	component: CompaniesPage,
});

const PAGE_SIZE = 50;
const ALL = "__all";

type CompanySort = "name" | "type" | "openDeals" | "owner";

function CompaniesPage(): ReactElement {
	const { t } = useTranslation();
	const { canManage } = useCrmPermissions();
	const { data: session } = useSession();
	const [search, setSearch] = useState("");
	const [debouncedSearch, setDebouncedSearch] = useState("");
	const [type, setType] = useState<CrmOrganizationType | null>(null);
	const [tag, setTag] = useState("");
	const [owner, setOwner] = useState<OwnerFilterValue>("all");
	const [newContactOpen, setNewContactOpen] = useState(false);
	const [newCompanyOpen, setNewCompanyOpen] = useState(false);
	const sorting = useListSort<CompanySort>("name", {
		descFirst: ["openDeals"],
	});

	useEffect(() => {
		const timer = setTimeout(() => {
			setDebouncedSearch(search.trim());
		}, 300);
		return () => clearTimeout(timer);
	}, [search]);

	const {
		data,
		isLoading,
		isError,
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage,
	} = useInfiniteQuery({
		...orpc.admin.crm.organizations.list.infiniteOptions({
			input: (offset: number) => ({
				search: debouncedSearch || undefined,
				type: type ?? undefined,
				tags: tag.trim() ? [tag.trim()] : undefined,
				ownerId: resolveOwnerFilter(owner, session?.user.id),
				sort: sorting.sort,
				direction: sorting.direction,
				limit: PAGE_SIZE,
				offset,
			}),
			initialPageParam: 0,
			getNextPageParam: (lastPage, pages) =>
				nextOffset(pages, lastPage.total, (page) => page.organizations.length),
		}),
		staleTime: CRM_STALE_TIME,
		placeholderData: keepPreviousData,
	});

	const rows = data?.pages.flatMap((page) => page.organizations) ?? [];
	const total = data?.pages[0]?.total ?? 0;

	return (
		<div className="space-y-4">
			<CrmPageHeader
				title={t("admin:crm.companies.title")}
				subtitle={t("admin:crm.companies.subtitle")}
				actions={
					canManage && (
						<>
							<Button
								variant="outline"
								onClick={() => setNewCompanyOpen(true)}
								data-testid="new-company-button"
							>
								<Building2 className="h-4 w-4" />
								{t("admin:crm.companies.new_company")}
							</Button>
							<Button
								onClick={() => setNewContactOpen(true)}
								data-testid="new-contact-button"
							>
								<UserPlus className="h-4 w-4" />
								{t("admin:crm.contacts.new_contact")}
							</Button>
						</>
					)
				}
			/>
			<div className="flex flex-wrap gap-2">
				<Input
					placeholder={t("admin:crm.companies.search_placeholder")}
					aria-label={t("admin:crm.companies.search_placeholder")}
					value={search}
					onChange={(event) => setSearch(event.target.value)}
					className="max-w-sm"
				/>
				<Select
					value={type ?? ALL}
					onValueChange={(value) =>
						setType(value === ALL ? null : (value as CrmOrganizationType))
					}
				>
					<SelectTrigger
						className="w-44"
						aria-label={t("admin:crm.company.type")}
					>
						<SelectValue />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value={ALL}>
							{t("admin:crm.companies.all_types")}
						</SelectItem>
						{CRM_ORGANIZATION_TYPES.map((value) => (
							<SelectItem key={value} value={value}>
								{t(`admin:crm.org_type.${value}`)}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
				<Input
					placeholder={t("admin:crm.companies.tag_filter")}
					aria-label={t("admin:crm.companies.tag_filter")}
					value={tag}
					onChange={(event) => setTag(event.target.value)}
					className="w-40"
				/>
				<OwnerFilter value={owner} onChange={setOwner} />
			</div>
			{isLoading ? (
				<div className="space-y-2">
					<Skeleton className="h-10" />
					<Skeleton className="h-10" />
					<Skeleton className="h-10" />
				</div>
			) : isError || !data ? (
				<CrmLoadError />
			) : rows.length === 0 ? (
				<p className="py-8 text-center text-muted-foreground text-sm">
					{t("admin:crm.companies.empty")}
				</p>
			) : (
				<>
					<Table>
						<TableHeader>
							<TableRow>
								<SortableHead
									label={t("admin:crm.company.name")}
									sortKey="name"
									{...sorting}
								/>
								<TableHead>{t("admin:crm.company.business_id")}</TableHead>
								<SortableHead
									label={t("admin:crm.company.type")}
									sortKey="type"
									{...sorting}
								/>
								<TableHead className="text-right">
									{t("admin:crm.companies.contacts")}
								</TableHead>
								<SortableHead
									label={t("admin:crm.companies.open_deals")}
									sortKey="openDeals"
									className="text-right"
									{...sorting}
								/>
								<SortableHead
									label={t("admin:crm.common.owner")}
									sortKey="owner"
									{...sorting}
								/>
							</TableRow>
						</TableHeader>
						<TableBody>
							{rows.map((org) => (
								<TableRow key={org.id}>
									<TableCell>
										<Link
											to="/crm/organizations/$organizationId"
											params={{ organizationId: org.id }}
											className="font-medium hover:underline"
										>
											{org.name}
										</Link>
										{org.parentName && (
											<p className="text-muted-foreground text-xs">
												{t("admin:crm.group.part_of", { name: org.parentName })}
											</p>
										)}
										{org.tags.length > 0 && (
											<div className="mt-1 flex flex-wrap gap-1">
												{org.tags.map((value) => (
													<Badge
														key={value}
														variant="secondary"
														className="text-[10px]"
													>
														{value}
													</Badge>
												))}
											</div>
										)}
									</TableCell>
									<TableCell>{org.businessId ?? "—"}</TableCell>
									<TableCell>
										<OrgTypeBadge type={org.type} />
									</TableCell>
									<TableCell className="text-right">
										{org.contactCount}
									</TableCell>
									<TableCell className="text-right">
										{org.openOpportunityCount}
									</TableCell>
									<TableCell>{org.ownerName ?? "—"}</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
					<LoadMore
						loaded={rows.length}
						total={total}
						hasMore={hasNextPage}
						loading={isFetchingNextPage}
						onLoadMore={() => void fetchNextPage()}
					/>
				</>
			)}
			<ContactFormDialog
				open={newContactOpen}
				onOpenChange={setNewContactOpen}
			/>
			<CompanyDialog
				open={newCompanyOpen}
				onOpenChange={setNewCompanyOpen}
				defaultName={search.trim()}
			/>
		</div>
	);
}
